import { getProcessingDealHighWater } from "tap/utils/sortStatusMessageLines.js";
import { scalarizeJobTaskFieldValue } from "tap/utils/normalizeLicensingJobViewTask.js";

const SEARCH_TASK_LABEL = "search transactional deals";

/**
 * Returns whether a job task represents the Search Transactional Deals step.
 * @param {Object} task
 * @returns {boolean}
 */
export function isSearchTransactionalDealsTask(task) {
  return String(task?.taskDetails ?? "")
    .toLowerCase()
    .includes(SEARCH_TASK_LABEL);
}

/**
 * Splits a status message into lines (handles escaped newlines).
 * @param {unknown} statusMessage
 * @returns {string[]}
 */
function statusMessageToLines(statusMessage) {
  const message = String(scalarizeJobTaskFieldValue(statusMessage) ?? "")
    .replace(/\\n/g, "\n")
    .trim();
  if (!message) {
    return [];
  }
  return message.split("\n");
}

/**
 * Max deal index from search-task status message processing lines.
 * @param {Array} jobTasks
 * @returns {number}
 */
export function getSearchTaskMessageHighWater(jobTasks = []) {
  let max = 0;

  jobTasks.filter(isSearchTransactionalDealsTask).forEach((task) => {
    statusMessageToLines(task?.statusMessage).forEach((line) => {
      const highWater = getProcessingDealHighWater(line);
      if (highWater != null && highWater > max) {
        max = highWater;
      }
    });
  });

  return max;
}

/**
 * Computes deal-search progress for the Job View modal header.
 * @param {Object|null|undefined} selectedJob
 * @param {Array} [jobTasks]
 * @returns {{ visible: false } | { visible: true, percent: number, progressCount: number, targetCount: number, isComplete: boolean }}
 */
export function getJobViewDealSearchProgress(selectedJob, jobTasks = []) {
  const targetCount = Number(selectedJob?.targetCount) || 0;
  const apiProgressCount = Number(selectedJob?.progressCount) || 0;

  if (targetCount <= 0) {
    return { visible: false };
  }

  const hasSearchTask = jobTasks.some(isSearchTransactionalDealsTask);
  const isApplicable =
    hasSearchTask ||
    apiProgressCount > 0 ||
    selectedJob?.jobStatus === "COMPLETE";

  if (!isApplicable) {
    return { visible: false };
  }

  const messageHighWater = getSearchTaskMessageHighWater(jobTasks);
  const progressCount = Math.min(
    targetCount,
    Math.max(apiProgressCount, messageHighWater),
  );

  const percent = Math.min(
    100,
    Math.round((progressCount / targetCount) * 100),
  );
  const isComplete =
    percent >= 100 || selectedJob?.jobStatus === "COMPLETE";

  return {
    visible: true,
    percent,
    progressCount,
    targetCount,
    isComplete,
  };
}
